import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import EmailStatusBadge from '@/components/shared/EmailStatusBadge';
import EmailDetailModal, { EmailDetails } from '@/components/shared/EmailDetailModal';
import EmailBodyPreview from '@/components/shared/EmailBodyPreview';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail, ChevronDown, Paperclip, FileText, Receipt, Maximize2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

interface CustomerEmailHistoryProps {
  customerId: string;
}

interface EmailSendWithDetails {
  id: string;
  document_type: string;
  document_id: string;
  version_id: string | null;
  recipient_email: string;
  recipient_name: string | null;
  subject: string;
  body_html: string | null;
  sent_at: string;
  status: string;
  delivered_at: string | null;
  opened_at: string | null;
  open_count: number | null;
  clicked_at: string | null;
  click_count: number | null;
  bounced_at: string | null;
  bounce_reason: string | null;
  // Joined data
  estimate_number?: string | null;
  invoice_number?: string | null;
  version_number?: number | null;
  version_type?: string | null;
}

const CustomerEmailHistory: React.FC<CustomerEmailHistoryProps> = ({ customerId }) => {
  const [selectedEmail, setSelectedEmail] = useState<EmailSendWithDetails | null>(null);

  // Fetch all estimates for this customer
  const { data: estimates } = useQuery({
    queryKey: ['customer-estimates-for-email', customerId],
    queryFn: async () => {
      const { data } = await supabase
        .from('estimates')
        .select('id, estimate_number')
        .eq('customer_id', customerId);
      return data || [];
    },
  });

  // Fetch all invoices for this customer
  const { data: invoices } = useQuery({
    queryKey: ['customer-invoices-for-email', customerId],
    queryFn: async () => {
      const { data } = await supabase
        .from('invoices')
        .select('id, invoice_number')
        .eq('customer_id', customerId);
      return data || [];
    },
  });

  // Fetch all email sends for these documents
  const { data: emailSends, isLoading } = useQuery({
    queryKey: ['customer-email-history', customerId, estimates?.length, invoices?.length],
    queryFn: async () => {
      const estimateIds = estimates?.map(e => e.id) || [];
      const invoiceIds = invoices?.map(i => i.id) || [];
      const allDocIds = [...estimateIds, ...invoiceIds];
      
      if (allDocIds.length === 0) return [];
      
      const { data, error } = await supabase
        .from('document_sends')
        .select('*')
        .in('document_id', allDocIds)
        .order('sent_at', { ascending: false });
      
      if (error) throw error;

      // Enrich with document numbers and version info
      const enrichedData: EmailSendWithDetails[] = await Promise.all(
        (data || []).map(async (send) => {
          const estimate = estimates?.find(e => e.id === send.document_id);
          const invoice = invoices?.find(i => i.id === send.document_id);
          
          let versionInfo = null;
          if (send.version_id) {
            const { data: version } = await supabase
              .from('estimate_versions')
              .select('version_number, version_type')
              .eq('id', send.version_id)
              .single();
            versionInfo = version;
          }

          return {
            ...send,
            estimate_number: estimate?.estimate_number,
            invoice_number: invoice?.invoice_number,
            version_number: versionInfo?.version_number,
            version_type: versionInfo?.version_type,
          };
        })
      );

      return enrichedData;
    },
    enabled: !!estimates && !!invoices,
  });

  // Handle URL hash to auto-open specific email
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.startsWith('#email-') && emailSends) {
      const emailId = hash.replace('#email-', '');
      const targetEmail = emailSends.find(e => e.id === emailId);
      if (targetEmail) {
        setSelectedEmail(targetEmail);
        // Clear hash after opening
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }
  }, [emailSends]);

  const convertToEmailDetails = (send: EmailSendWithDetails): EmailDetails => ({
    id: send.id,
    document_type: send.document_type,
    document_id: send.document_id,
    version_id: send.version_id,
    recipient_email: send.recipient_email,
    recipient_name: send.recipient_name,
    subject: send.subject,
    body_html: send.body_html,
    sent_at: send.sent_at,
    status: send.status,
    delivered_at: send.delivered_at,
    opened_at: send.opened_at,
    open_count: send.open_count,
    clicked_at: send.clicked_at,
    click_count: send.click_count,
    bounced_at: send.bounced_at,
    bounce_reason: send.bounce_reason,
    document_number: send.estimate_number || send.invoice_number,
    version_number: send.version_number,
    version_type: send.version_type,
  });

  if (isLoading || !estimates || !invoices) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Email History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!emailSends || emailSends.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Email History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm">No emails sent to this customer yet</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Email History
            <Badge variant="secondary" className="ml-auto">{emailSends.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {emailSends.map((send) => (
              <EmailHistoryItem 
                key={send.id} 
                send={send} 
                onViewFull={() => setSelectedEmail(send)}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      <EmailDetailModal
        open={!!selectedEmail}
        onOpenChange={(open) => !open && setSelectedEmail(null)}
        email={selectedEmail ? convertToEmailDetails(selectedEmail) : null}
      />
    </>
  );
};

const EmailHistoryItem: React.FC<{ 
  send: EmailSendWithDetails;
  onViewFull: () => void;
}> = ({ send, onViewFull }) => {
  const [expanded, setExpanded] = useState(false);

  const getDocumentLabel = () => {
    if (send.document_type === 'invoice') {
      return send.invoice_number ? `Invoice ${send.invoice_number}` : 'Invoice';
    }
    const baseLabel = send.estimate_number ? `Estimate ${send.estimate_number}` : 'Estimate';
    return baseLabel;
  };

  const getVersionLabel = () => {
    if (!send.version_number) return null;
    if (send.version_type === 'change_order') {
      return `Change Order v${send.version_number}`;
    }
    return `Version ${send.version_number}`;
  };

  const getVersionBadgeLabel = () => {
    if (!send.version_number) return null;
    if (send.version_type === 'change_order') {
      return `CO v${send.version_number}`;
    }
    return `v${send.version_number}`;
  };

  const DocumentIcon = send.document_type === 'invoice' ? Receipt : FileText;

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <div className="p-3 bg-muted/50 rounded-lg overflow-hidden">
        <CollapsibleTrigger className="w-full text-left">
          <div className="flex items-start justify-between gap-2 min-w-0">
            <div className="space-y-1 min-w-0 flex-1 overflow-hidden">
              <div className="font-medium text-sm flex items-center gap-2 min-w-0">
                <DocumentIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="truncate">{getDocumentLabel()}</span>
                {send.version_number && (
                  <Badge variant="outline" className="text-xs shrink-0">
                    {getVersionBadgeLabel()}
                  </Badge>
                )}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                To: {send.recipient_email}
              </div>
              <div className="text-xs text-muted-foreground">
                {format(new Date(send.sent_at), 'dd MMMM yyyy HH:mm')}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {send.subject}
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0 ml-2 mr-2">
              <EmailStatusBadge 
                status={send.status as any}
                sentAt={send.sent_at}
                deliveredAt={send.delivered_at}
                openedAt={send.opened_at}
                clickedAt={send.clicked_at}
                bouncedAt={send.bounced_at}
                openCount={send.open_count || 0}
                clickCount={send.click_count || 0}
                bounceReason={send.bounce_reason}
                showDetails
              />
              <ChevronDown className={cn("h-4 w-4 transition-transform", expanded && "rotate-180")} />
            </div>
          </div>
        </CollapsibleTrigger>
        
        <CollapsibleContent className="pt-3 mt-3 border-t border-border">
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Subject</Label>
              <p className="text-sm mt-1">{send.subject}</p>
            </div>
            
            <div>
              <Label className="text-xs text-muted-foreground">Email Body Preview</Label>
              <div className="mt-1">
                <EmailBodyPreview html={send.body_html} height={200} />
              </div>
            </div>
            
            {send.version_number && (
              <div>
                <Label className="text-xs text-muted-foreground">Attachment</Label>
                <div className="flex items-center gap-2 mt-1 text-sm">
                  <Paperclip className="h-4 w-4 text-muted-foreground" />
                  <span>{getVersionLabel()} (PDF)</span>
                </div>
              </div>
            )}

            <div className="pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewFull();
                }}
              >
                <Maximize2 className="h-3.5 w-3.5 mr-1.5" />
                View Full Email
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export default CustomerEmailHistory;
